/**
 * ResetPasswordPage.tsx
 *
 * Landing page for the link sent in the forgot-password email. Reads the
 * token from the URL (?token=...) and lets the user set a new password.
 */
import { useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { authApi } from '@/services/api';
import { getLegacyOrigin } from '@/lib/appStage';

const PROSPERA_LOGO_URL = `${getLegacyOrigin()}/uimages/img8/brandlogo.png`;
const MIN_LENGTH = 10;

interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  tone: string;
}

function checkStrength(pw: string): PasswordStrength {
  let score = 0;
  if (pw.length >= MIN_LENGTH) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const labels = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'] as const;
  const tones = [
    'bg-slate-500',
    'bg-red-500',
    'bg-amber-500',
    'bg-yellow-400',
    'bg-emerald-500',
  ] as const;
  return { score: score as PasswordStrength['score'], label: labels[score], tone: tones[score] };
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const strength = useMemo(() => checkStrength(password), [password]);
  const passwordsMatch = password === confirm;
  const isReady = !!token && password.length >= MIN_LENGTH && passwordsMatch && strength.score >= 2;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('This reset link is missing a token. Please request a new one.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err: unknown) {
      setError(
        (err as { userMessage?: string })?.userMessage
          ?? 'This reset link is invalid or has expired. Request a new one.',
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#040816] px-4 py-10 text-white">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <img
            src={PROSPERA_LOGO_URL}
            alt="Prospera"
            className="h-auto w-full max-w-[280px] object-contain"
          />
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#141a2b] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.45)] space-y-5">
          <div>
            <h1 className="text-lg font-semibold text-white">Set a new password</h1>
            <p className="mt-1 text-sm text-slate-400">
              Choose a strong password you don't use anywhere else.
            </p>
          </div>

          {!token && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
              This page needs a reset token. Open the link from your email, or{' '}
              <Link to="/forgot-password" className="underline hover:no-underline">
                request a new one
              </Link>.
            </div>
          )}

          {done ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>Password updated. Redirecting you to sign in…</div>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="password" className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300">
                    New password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                      autoFocus
                      disabled={!token}
                      minLength={MIN_LENGTH}
                      className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 pr-10 text-sm text-white placeholder:text-slate-500 focus:border-[#6558f5] focus:outline-none focus:ring-2 focus:ring-[#6558f5]/40 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder={`At least ${MIN_LENGTH} characters`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                      tabIndex={-1}
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  {/* Strength meter */}
                  {password.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <div className="flex gap-1">
                        {[0, 1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded-full ${
                              i < strength.score ? strength.tone : 'bg-white/10'
                            }`}
                          />
                        ))}
                      </div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-400">
                        Strength: <span className="text-slate-300">{strength.label}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="confirm" className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300">
                    Confirm password
                  </label>
                  <input
                    id="confirm"
                    type={showPw ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                    disabled={!token}
                    className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#6558f5] focus:outline-none focus:ring-2 focus:ring-[#6558f5]/40 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Type it again"
                  />
                  {confirm.length > 0 && !passwordsMatch && (
                    <p className="mt-1 text-xs text-red-300">Passwords don't match.</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !isReady}
                  className="w-full rounded-md bg-[#6558f5] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#766af8] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Updating…' : 'Update password'}
                </button>
              </form>

              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
